import { AxiosError, AxiosResponse } from 'axios'
import ThaiDatePicker from 'common/components/ThaiDatePicker'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import useMedia from 'common/hooks/useMedia'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import {
  formatDate,
  formatThaiDate,
  getDateFromInputDateTime,
  getMaxTimeFromInputDate,
} from 'common/utils/date'
import { errorMessageByField } from 'common/utils/hook-form'
import endOfToday from 'date-fns/endOfToday'
import parseISO from 'date-fns/parseISO'
import isEmpty from 'lodash/isEmpty'
import round from 'lodash/round'
import {
  useMutateCreateDeadShrimp,
  useMutatePatchDeadShrimp,
} from 'modules/deadShrimp/api/mutation'
import {
  useGetDeadShrimpLineChart,
  useGetRecentSize,
  useGetShrimpDiseases,
} from 'modules/deadShrimp/api/queries'
import { useDeadShrimps, useFindDeadShrimpById } from 'modules/deadShrimp/api/selector'
import { DISEASE_OTHER, ShrimpDeadAmountUnit } from 'modules/deadShrimp/constants'
import { deadShrimpDateFilterState } from 'modules/deadShrimp/stores/atom'
import { useGrowthShrimps } from 'modules/growth/api/selector'
import { useIsFishSpecie, useSpeciesName } from 'modules/pond/api/selector'
import BaseRecordDialog from 'modules/pond/components/BaseRecordDialog'
import { SpeciesName } from 'modules/pond/constants'
import { useGetCRUDByFeature } from 'modules/user/api/selector'
import { useEffect, useState, useRef, useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'
import { normalizeSpeciesName } from 'utils/analytics'
import * as Yup from 'yup'

import CheckIcon from '@mui/icons-material/Check'
import {
  MenuItem,
  TextField,
  Stack,
  Typography,
  useTheme,
  Box,
  Paper,
  Select,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'

import { Icon } from '@iconify/react'

import { fDateDashTime } from 'utils/formatTime'

interface DeadShrimpDialogProps {
  onClose: () => void
  open: boolean
  isEdit?: boolean
}

interface ImageWithPreview extends File {
  preview?: string
}

interface FormDataType {
  date: Date
  time: Date
  size: number | undefined
  amountNew: number | undefined
  newUnit: ShrimpDeadAmountUnit
  amountOld: number | undefined
  oldUnit: ShrimpDeadAmountUnit
  causeOfDeath: string
  causeOfDeathOther: string
  images: ImageWithPreview[]
}

const DeadShrimpDialog = ({ onClose, open, isEdit = false }: DeadShrimpDialogProps) => {
  const { t } = useTranslation('deadshrimp')
  const theme = useTheme()
  const isMobile = useMedia('sm')

  const [submitting, setSubmitting] = useState(false)
  const [previewOpen, setPreviewOpen] = useState<number | false>(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isEditingSize, setIsEditingSize] = useState(false)
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([])

  const dateFilter = useRecoilValue(deadShrimpDateFilterState)

  const growthCrud = useGetCRUDByFeature(FeatureKey.Growth)
  const hasGrowthReadAccess = growthCrud?.[CRUDAction.Read] === 1
  const { data: shrimpGrowth } = useGrowthShrimps(hasGrowthReadAccess)
  const { refetch: refetchCard } = useDeadShrimps('card')
  const { refetch: refetchTable } = useDeadShrimps('table')
  const { refetch: refetchChart } = useGetDeadShrimpLineChart()
  const deadShrimpData = useFindDeadShrimpById()

  const { data: shrimpDiseases } = useGetShrimpDiseases()
  const speciesName = useSpeciesName()
  const isFish = useIsFishSpecie()

  const createMutation = useMutateCreateDeadShrimp()
  const updateMutation = useMutatePatchDeadShrimp()
  const { trackEvent } = useAnalytics()

  const hasShrimpGrowth = !isEmpty(shrimpGrowth)
  const decimalRegex = /^\d+(\.\d+)?$/
  const decimalAllowZero = () =>
    Yup.string()
      .nullable()
      .transform((value) => (value === '' ? null : value))
      .matches(decimalRegex, t('require.number'))

  const schema = Yup.object()
    .shape({
      size: decimalAllowZero().required(t('require.size')),

      amountNew: decimalAllowZero(),
      amountOld: decimalAllowZero(),

      newUnit: Yup.string().required(),
      oldUnit: Yup.string().required(),

      causeOfDeath: Yup.string().required(t('require.reason')),
      causeOfDeathOther: Yup.string().when('causeOfDeath', {
        is: DISEASE_OTHER,
        then: (schema) => schema.required(t('require.specify.other')),
        otherwise: (schema) => schema.notRequired(),
      }),
    })
    .test('one-required', t('one.required'), (values) => {
      return !!(values.amountNew || values.amountOld)
    })
  const resolver = useYupValidationResolver(schema)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
    setError,
  } = useForm<FormDataType>({
    defaultValues: {
      date: new Date(),
      time: new Date(),
      size: undefined,
      amountNew: undefined,
      newUnit: ShrimpDeadAmountUnit.Count,
      amountOld: undefined,
      oldUnit: ShrimpDeadAmountUnit.Count,
      images: [],
      causeOfDeath: '',
      causeOfDeathOther: '',
    },
    resolver,
  })

  const { data: recentSize } = useGetRecentSize(formatDate(watch('date')))
  // Picking "ตัว" only needs a size to convert with. The growth list needs the
  // Growth permission, so staff with only dead-shrimp access were stuck on kg;
  // the mortality recent-size (dead-shrimp permission) covers them too.
  const hasSizeData = hasShrimpGrowth || recentSize?.size != null

  useEffect(() => {
    if (dateFilter) {
      setValue('date', dateFilter)
      setValue('time', dateFilter)
    }
  }, [dateFilter, setValue])

  useEffect(() => {
    if (!isEdit && recentSize !== undefined) {
      setValue('size', recentSize.size ?? undefined)
      setIsEditingSize(false)
    }
  }, [recentSize, isEdit, setValue])

  useEffect(() => {
    if (deadShrimpData && isEdit) {
      const datetime = parseISO(deadShrimpData.datetime)
      const isOther =
        deadShrimpData.causeOfDeath &&
        !shrimpDiseases?.some((d) => d.name === deadShrimpData.causeOfDeath)

      reset({
        date: datetime,
        time: datetime,
        size: deadShrimpData.shrimpCountInKg,
        amountNew: round(deadShrimpData.newDeadShrimpCountInKg, 2),
        newUnit: ShrimpDeadAmountUnit.Kg,
        amountOld: round(deadShrimpData.oldDeadShrimpCountInKg, 2),
        oldUnit: ShrimpDeadAmountUnit.Kg,
        images: [],
        causeOfDeath: isOther ? DISEASE_OTHER : deadShrimpData.causeOfDeath || '',
        causeOfDeathOther: isOther ? deadShrimpData.causeOfDeath : '',
      })

      setExistingImageUrls(deadShrimpData.img_urls ?? [])
    }
  }, [deadShrimpData, isEdit, recentSize, reset, shrimpDiseases])

  useEffect(() => {
    if (isEdit) {
      setValue('newUnit', ShrimpDeadAmountUnit.Kg)
      setValue('oldUnit', ShrimpDeadAmountUnit.Kg)
      return
    }

    // Shrimp is counted in kg, fish by the head. Counting needs a size to
    // convert with, so without one the unit stays kg (the picker is locked).
    const defaultUnit =
      isFish && hasSizeData ? ShrimpDeadAmountUnit.Count : ShrimpDeadAmountUnit.Kg

    setValue('newUnit', defaultUnit)
    setValue('oldUnit', defaultUnit)
  }, [hasSizeData, isEdit, isFish, setValue])

  const refetchAllData = useCallback(() => {
    setTimeout(() => {
      refetchCard()
      refetchChart()
      refetchTable()
    }, 1000)
  }, [refetchCard, refetchChart, refetchTable])

  const handleSuccess = useCallback(
    (res: AxiosResponse<any>, causePresent: boolean, isEditAction: boolean) => {
      const { status } = res.data
      if (status === 200) {
        setSubmitting(false)
        onClose()
        reset()
        refetchAllData()
        const normalizedSpecies = normalizeSpeciesName(speciesName)
        // Stringify to match mobile (Firebase Analytics params are
        // String/num only, and GA4 should see the same value type for
        // `cause_present` on both platforms).
        trackEvent(
          isEditAction ? AnalyticsEvents.mortalityRecordUpdated : AnalyticsEvents.mortalityRecordCreated,
          {
            cause_present: causePresent.toString(),
            ...(normalizedSpecies ? { species: normalizedSpecies } : {}),
          }
        )
      }
    },
    [onClose, reset, refetchAllData, speciesName, trackEvent]
  )

  const handleError = useCallback(
    (error: unknown) => {
      const { response } = error as AxiosError
      if (response?.status === 400) {
        setError('time', {
          type: 'server',
          message: t('duplicate.time'),
        })
      }
      setSubmitting(false)
    },
    [setError, t]
  )

  const onSubmit = async (data: FormDataType) => {
    setSubmitting(true)

    try {
      const combinedDate = getDateFromInputDateTime(data.date, data.time)
      const datetime = fDateDashTime(combinedDate)

      let imgFiles: File[] = []
      if (data.images && data.images.length > 0) {
        imgFiles = data.images
      }

      const imageUrls = isEdit && existingImageUrls.length > 0 ? existingImageUrls : []

      const causePresent = data.causeOfDeath === DISEASE_OTHER && !!data.causeOfDeathOther

      const payload = {
        datetime,
        newDeadShrimpCount: data.amountNew || 0,
        newDeadShrimpCountUnit: data.amountNew ? data.newUnit : ShrimpDeadAmountUnit.Kg,
        oldDeadShrimpCount: data.amountOld || 0,
        oldDeadShrimpCountUnit: data.amountOld ? data.oldUnit : ShrimpDeadAmountUnit.Kg,
        causeOfDeath:
          data.causeOfDeath === DISEASE_OTHER ? data.causeOfDeathOther : data.causeOfDeath,
        size: data.size || 0,
        images: imgFiles,
        imageUrls: imageUrls,
      }

      if (isEdit && deadShrimpData?.id) {
        const params = {
          deathId: Number(deadShrimpData.id),
          body: payload,
        }

        await updateMutation.mutateAsync(params, {
          onSuccess: (res) => handleSuccess(res, causePresent, true),
          onError: handleError,
        })
      } else {
        await createMutation.mutateAsync(
          { body: payload },
          {
            onSuccess: (res) => handleSuccess(res, causePresent, false),
            onError: handleError,
          }
        )
      }
    } catch (error) {
      console.error('Submit error:', error)
      setSubmitting(false)
    }
  }

  const handleToggleUnit = useCallback(
    (newValue: ShrimpDeadAmountUnit, field: 'newUnit' | 'oldUnit') => {
      setValue(field, newValue)
    },
    [setValue]
  )

  const handleRemoveImage = useCallback(
    (index: number) => {
      const uploaded = watch('images') || []

      const existingCount = existingImageUrls.length
      if (index < existingCount) {
        setExistingImageUrls((prev) => prev.filter((_, i) => i !== index))
      } else {
        const uploadIndex = index - existingCount
        setValue(
          'images',
          uploaded.filter((_, i: number) => i !== uploadIndex)
        )
      }
    },
    [existingImageUrls.length, setValue, watch]
  )

  const handleClosePreview = useCallback(() => {
    setPreviewOpen(false)
  }, [])

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files
      if (files && files.length > 0) {
        const newFiles: ImageWithPreview[] = Array.from(files).map((file: ImageWithPreview) => {
          file.preview = URL.createObjectURL(file)
          return file
        })
        setValue('images', [...(watch('images') || []), ...newFiles])
      }
      event.target.value = ''
    },
    [setValue, watch]
  )

  const renderImageUpload = useCallback(
    (field: any) => {
      const uploaded: any[] = field.value || []
      const existing = existingImageUrls || []

      const combined = [
        ...existing.map((url) => ({ src: url, isRemote: true })),
        ...uploaded.map((f: any) => ({ src: f.preview, isRemote: false })),
      ]

      if (combined.length === 0) {
        return (
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              minHeight: 120,
              borderRadius: 2,
              border: '2px dashed',
              borderColor: theme.palette.divider,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#fafafa',
              cursor: 'pointer',
              flexDirection: 'column',
              gap: 1,
            }}
            onClick={handleUploadClick}
          >
            <img src="/static/icons/pond/upload_image.svg" alt="" />
            <Typography color="text.secondary" fontSize={16}>
              {t('click.or.drag.to.upload')}
            </Typography>
            <Typography color="text.secondary" fontSize={13}>
              {t('supported.file.types')}
            </Typography>
          </Paper>
        )
      }

      return (
        <Box>
          <Grid container spacing={2}>
            {combined.map((item, idx) => (
              <Grid item xs={6} sm={4} md={3} key={idx}>
                <Paper
                  elevation={0}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '1/1',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '2px solid',
                    borderColor: theme.palette.divider,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    background: '#f8f8f8',
                  }}
                  onClick={() => setPreviewOpen(idx)}
                >
                  <img
                    src={item.src}
                    alt={`uploaded-${idx}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      bgcolor: 'rgba(0,0,0,0.35)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      opacity: 1,
                    }}
                  >
                    <IconButton
                      aria-label="preview"
                      onClick={(e) => {
                        e.stopPropagation()
                        setPreviewOpen(idx)
                      }}
                      sx={{
                        color: theme.palette.common.white,
                        bgcolor: 'rgba(0,0,0,0.25)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.45)' },
                      }}
                    >
                      <Icon icon="mdi:eye-outline" width={28} height={28} />
                    </IconButton>
                    <IconButton
                      aria-label="delete"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemoveImage(idx)
                      }}
                      sx={{
                        color: theme.palette.common.white,
                        bgcolor: 'rgba(0,0,0,0.25)',
                        '&:hover': { bgcolor: theme.palette.error.main },
                      }}
                    >
                      <Icon icon="mdi:trash-can-outline" width={28} height={28} />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            ))}
            {/* Upload more */}
            <Grid item xs={6} sm={4} md={3}>
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  aspectRatio: '1/1',
                  borderRadius: 2,
                  border: '2px dashed',
                  borderColor: theme.palette.divider,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#fafafa',
                  cursor: 'pointer',
                }}
                onClick={handleUploadClick}
              >
                <Stack alignItems="center">
                  <Typography color="text.secondary" fontSize={32}>
                    +
                  </Typography>
                  <Typography color="text.secondary" fontSize={14}>
                    {t('upload')}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )
    },
    [existingImageUrls, theme, handleRemoveImage, handleUploadClick, t]
  )

  // Conversion helpers
  const convertCountToKg = (count: number, size: number) => {
    if (!count || !size) return 0
    return round(isFish ? (count * size) / 1000 : count / size, 2)
  }

  const convertKgToCount = (kg: number, size: number) => {
    if (!kg || !size) return 0
    return round(isFish ? (kg * 1000) / size : kg * size, 2)
  }

  // Common disabled input style
  const getInputStyle = (disabled: boolean) => ({
    fontSize: 16,
    border: 'none',
    paddingLeft: 8,
    height: 30,
    backgroundColor: disabled ? theme.palette.grey[200] : theme.palette.common.white,
  })

  const sizeTypeMap: Record<SpeciesName, string> = {
    [SpeciesName.Shrimp]: t('unit.per.kg'),
    [SpeciesName.Fish]: t('gram.per.unit'),
  }

  return (
    <BaseRecordDialog
      onClose={onClose}
      open={open}
      formId="record-deadshrimp"
      title={
        isEdit
          ? t('edit.dead.shrimp', { speciesName })
          : t('record.dead.species', { speciesName })
      }
      isSubmitting={submitting}
    >
      <form id="record-deadshrimp" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          {/* Date and Time */}
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={2}
            alignItems={isMobile ? 'flex-start' : 'center'}
            justifyContent="space-between"
          >
            <Stack flex={1}>
              <Typography fontWeight="bold">
                <Typography mr={0.5} component="span" color={theme.palette.error.main}>
                  *
                </Typography>
                {t('date')}
              </Typography>
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <ThaiDatePicker
                    {...field}
                    format="dd/MM/yyyy"
                    maxDate={endOfToday()}
                    slotProps={{
                      textField: {
                        id: 'dead-shrimp-date-picker',
                        size: 'small',
                        error: !!errors.date,
                        helperText: errorMessageByField('date', errors),
                      },
                    }}
                  />
                )}
              />
            </Stack>

            <Stack flex={1}>
              <Typography fontWeight="bold">
                <Typography mr={0.5} component="span" color={theme.palette.error.main}>
                  *
                </Typography>
                {t('time')}
              </Typography>
              <Controller
                name="time"
                control={control}
                render={({ field }) => (
                  <TimePicker
                    {...field}
                    format="HH:mm"
                    views={['hours', 'minutes']}
                    timeSteps={{ minutes: 1 }}
                    maxTime={getMaxTimeFromInputDate(watch('date'))}
                    ampm={false}
                    slotProps={{
                      textField: {
                        id: 'dead-shrimp-time-picker',
                        size: 'small',
                        error: !!errors.time,
                        helperText: errorMessageByField('time', errors),
                      },
                    }}
                  />
                )}
              />
            </Stack>
          </Stack>

          {/* Size */}
          <Stack>
            <Typography fontWeight="bold">
              <Typography mr={0.5} component={'span'} color={theme.palette.error.main}>
                *
              </Typography>
              {t('size')}
            </Typography>
            <Controller
              name="size"
              control={control}
              render={({ field }) => (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: 1,
                      border: '1.5px solid',
                      borderColor: errors.size ? theme.palette.error.main : 'divider',
                      overflow: 'hidden',
                    }}
                  >
                    <TextField
                      {...field}
                      variant="standard"
                      fullWidth
                      type="text"
                      inputMode="decimal"
                      placeholder={t('fill.size')}
                      error={!!errors.size}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        style: getInputStyle(!isEditingSize),
                      }}
                      sx={{
                        '& .MuiInputBase-input': {
                          border: 'none',
                        },
                        '& .MuiInput-underline:before, & .MuiInput-underline:after': {
                          display: 'none',
                        },
                      }}
                      disabled={!isEditingSize}
                    />
                    <Box
                      component="span"
                      sx={{
                        color: 'text.primary',
                        typography: 'body1',
                        backgroundColor: theme.palette.grey[200],
                        borderLeft: `1px solid ${theme.palette.grey[200]}`,
                        borderColor: 'divider',
                        textAlign: 'center',
                        cursor: 'pointer',
                        p: 1,
                        whiteSpace: 'nowrap',
                      }}
                      onClick={() => setIsEditingSize((prev) => !prev)}
                    >
                      {sizeTypeMap[(speciesName as SpeciesName) ?? SpeciesName.Shrimp]}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        color: 'text.primary',
                        typography: 'body1',
                        backgroundColor: theme.palette.grey[200],
                        p: 1,
                        borderLeft: `1px solid ${theme.palette.grey[200]}`,
                        borderColor: 'divider',
                        minWidth: 45,
                        textAlign: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => setIsEditingSize((prev) => !prev)}
                    >
                      {isEditingSize ? (
                        <CheckIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
                      ) : (
                        <img src="/static/icons/pond/tabler_edit.svg" alt="" />
                      )}
                    </Box>
                  </Box>
                  {errors.size && (
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.error.main, mt: 0.5, ml: 1.5 }}
                    >
                      {errors.size.message}
                    </Typography>
                  )}
                </Box>
              )}
            />
            {!isEdit && recentSize?.date && recentSize.size === watch('size') && (
              <Box mt={1}>
                <Typography
                  variant="caption"
                  color={theme.palette.primary.dark}
                  sx={{
                    background: theme.palette.primary.lighter,
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  {t('size.latest.record', { date: formatThaiDate(recentSize.date) })}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Amount New and Old */}
          <Stack spacing={2} direction={isMobile ? 'column' : 'row'}>
            <Stack flex={1}>
              <Typography fontWeight="bold">{t('amount.new')}</Typography>
              <Controller
                name="amountNew"
                control={control}
                render={({ field }) => (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 1,
                        border: '1.5px solid',
                        borderColor: errors.amountNew ? theme.palette.error.main : 'divider',
                        overflow: 'hidden',
                      }}
                    >
                      <TextField
                        {...field}
                        variant="standard"
                        fullWidth
                        type="text"
                        inputMode="numeric"
                        placeholder={t('fill.number')}
                        error={!!errors.amountNew}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          style: getInputStyle(isEditingSize),
                        }}
                        sx={{
                          '& .MuiInputBase-root': { borderRadius: 0 },
                          '& .MuiInputBase-input': { border: 'none' },
                          '& .MuiInput-underline:before, & .MuiInput-underline:after': {
                            display: 'none',
                          },
                        }}
                        disabled={isEditingSize}
                      />
                      <Box
                        sx={{
                          backgroundColor: theme.palette.grey[200],
                          borderLeft: `1px solid ${theme.palette.grey[300]}`,
                          textAlign: 'center',
                          display: 'flex',
                          p: 0.5,
                          alignItems: 'center',
                        }}
                      >
                        <Select
                          value={watch('newUnit')}
                          onChange={(e) =>
                            handleToggleUnit(e.target.value as ShrimpDeadAmountUnit, 'newUnit')
                          }
                          size="small"
                          variant="standard"
                          disableUnderline
                          disabled={isEditingSize || !hasSizeData}
                          sx={{
                            background: 'transparent',
                            '& .MuiSelect-select': {
                              py: 0.5,
                              color:
                                isEditingSize || !hasSizeData
                                  ? theme.palette.text.disabled
                                  : 'inherit',
                            },
                          }}
                        >
                          <MenuItem value={ShrimpDeadAmountUnit.Count}>
                            {t('shrimp.per.kg')}
                          </MenuItem>
                          <MenuItem value={ShrimpDeadAmountUnit.Kg}>{t('kg')}</MenuItem>
                        </Select>
                      </Box>
                    </Box>
                    {errors.amountNew && (
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.error.main, mt: 0.5, ml: 1.5 }}
                      >
                        {errors.amountNew.message}
                      </Typography>
                    )}
                    <Box mt={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {(() => {
                          const size = watch('size')
                          const amount = field.value
                          const unit = watch('newUnit')
                          if (!amount || !size) return null
                          if (unit === ShrimpDeadAmountUnit.Count) {
                            const kg = convertCountToKg(amount, size)
                            return `≈ ${kg} ${t('kg')}`
                          } else {
                            const count = convertKgToCount(amount, size)
                            return `≈ ${count} ${t('shrimp.per.kg')}`
                          }
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Stack>

            <Stack flex={1}>
              <Typography fontWeight="bold">{t('amount.old')}</Typography>
              <Controller
                name="amountOld"
                control={control}
                render={({ field }) => (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 1,
                        border: '1.5px solid',
                        borderColor: errors.amountOld ? theme.palette.error.main : 'divider',
                        overflow: 'hidden',
                      }}
                    >
                      <TextField
                        {...field}
                        variant="standard"
                        fullWidth
                        type="text"
                        inputMode="numeric"
                        placeholder={t('fill.number')}
                        error={!!errors.amountOld}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          style: getInputStyle(isEditingSize),
                        }}
                        sx={{
                          '& .MuiInputBase-root': { borderRadius: 0 },
                          '& .MuiInputBase-input': { border: 'none' },
                          '& .MuiInput-underline:before, & .MuiInput-underline:after': {
                            display: 'none',
                          },
                        }}
                        disabled={isEditingSize}
                      />
                      <Box
                        sx={{
                          backgroundColor: theme.palette.grey[200],
                          borderLeft: `1px solid ${theme.palette.grey[300]}`,
                          textAlign: 'center',
                          display: 'flex',
                          p: 0.5,
                          alignItems: 'center',
                        }}
                      >
                        <Select
                          value={watch('oldUnit')}
                          onChange={(e) =>
                            handleToggleUnit(e.target.value as ShrimpDeadAmountUnit, 'oldUnit')
                          }
                          size="small"
                          variant="standard"
                          disableUnderline
                          disabled={isEditingSize || !hasSizeData}
                          sx={{
                            background: 'transparent',
                            '& .MuiSelect-select': {
                              py: 0.5,
                              color:
                                isEditingSize || !hasSizeData
                                  ? theme.palette.text.disabled
                                  : 'inherit',
                            },
                          }}
                        >
                          <MenuItem value={ShrimpDeadAmountUnit.Count}>
                            {t('shrimp.per.kg')}
                          </MenuItem>
                          <MenuItem value={ShrimpDeadAmountUnit.Kg}>{t('kg')}</MenuItem>
                        </Select>
                      </Box>
                    </Box>
                    {errors.amountOld && (
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.error.main, mt: 0.5, ml: 1.5 }}
                      >
                        {errors.amountOld.message}
                      </Typography>
                    )}
                    <Box mt={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {(() => {
                          const size = watch('size')
                          const amount = field.value
                          const unit = watch('oldUnit')
                          if (!amount || !size) return null
                          if (unit === ShrimpDeadAmountUnit.Count) {
                            const kg = convertCountToKg(amount, size)
                            return `≈ ${kg} ${t('kg')}`
                          } else {
                            const count = convertKgToCount(amount, size)
                            return `≈ ${count} ${t('shrimp.per.kg')}`
                          }
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Stack>
          </Stack>
          {/* Helper message if both are empty */}
          {!(watch('amountNew') || watch('amountOld')) && (
            <Typography variant="caption" color={theme.palette.error.main} sx={{ ml: 1, mt: 0 }}>
              {t('one.required')}
            </Typography>
          )}

          {/* Cause of Death */}
          <Stack>
            <Typography fontWeight="bold">
              <Typography mr={0.5} component="span" color={theme.palette.error.main}>
                *
              </Typography>
              {t('death.reason')}
            </Typography>
            <Controller
              name="causeOfDeath"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  error={!!errors.causeOfDeath}
                  helperText={errorMessageByField('causeOfDeath', errors)}
                  SelectProps={{
                    displayEmpty: true,
                  }}
                  fullWidth
                  size="small"
                  disabled={isEditingSize}
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: isEditingSize
                        ? theme.palette.grey[200]
                        : theme.palette.common.white,
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    {t('choose.death.reason')}
                  </MenuItem>
                  {(shrimpDiseases ?? []).map((disease) => (
                    <MenuItem value={disease.name} key={disease.id}>
                      {disease.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {/* Show text field when "อื่นๆ" is selected */}
            {watch('causeOfDeath') === DISEASE_OTHER && (
              <Box mt={1}>
                <Controller
                  name="causeOfDeathOther"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      placeholder={t('specify.other.reason')}
                      error={!!errors.causeOfDeathOther}
                      helperText={errorMessageByField('causeOfDeathOther', errors)}
                      disabled={isEditingSize}
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: isEditingSize
                            ? theme.palette.grey[200]
                            : theme.palette.common.white,
                        },
                      }}
                    />
                  )}
                />
              </Box>
            )}
          </Stack>

          {/* Image Upload */}
          <Stack>
            <Typography fontWeight="bold">{t('upload.photo')}</Typography>
            <Controller
              name="images"
              control={control}
              render={({ field }) => {
                const uploaded = field.value || []
                const existing = existingImageUrls || []
                const combined = [
                  ...existing.map((u) => ({ src: u, isRemote: true })),
                  ...uploaded.map((f: any) => ({ src: f.preview, isRemote: false })),
                ]

                return (
                  <Box>
                    {renderImageUpload(field)}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      multiple
                      onChange={handleFileChange}
                    />
                    <Dialog
                      open={previewOpen !== false}
                      onClose={handleClosePreview}
                      maxWidth="sm"
                      fullWidth
                    >
                      <DialogContent sx={{ p: 0, position: 'relative' }}>
                        <IconButton
                          aria-label="close"
                          onClick={handleClosePreview}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 2,
                            color: theme.palette.common.white,
                            bgcolor: theme.palette.grey[600],
                            '&:hover': { bgcolor: theme.palette.error.main },
                          }}
                        >
                          <Icon icon="mdi:close" width={28} height={28} />
                        </IconButton>
                        {previewOpen !== false && combined[previewOpen] && (
                          <img
                            src={combined[previewOpen].src}
                            alt="preview"
                            style={{ width: '100%', height: 'auto', display: 'block' }}
                          />
                        )}
                      </DialogContent>
                    </Dialog>
                  </Box>
                )
              }}
            />
          </Stack>
        </Stack>
      </form>
    </BaseRecordDialog>
  )
}

export default DeadShrimpDialog
